import React from 'react';
import { FieldWrapper } from './FieldWrapper';
import { getNestedValue } from './utils/formUtils';
import { FIELD_TYPES } from './constants/formConstants';

// Import all field components with named imports
import { TextField } from './fields/TextField';
import { PasswordField } from './fields/PasswordField';
import { TextareaField } from './fields/TextareaField';
import { SelectField } from './fields/SelectField';
import { CheckboxField } from './fields/CheckboxField';
import { RadioField } from './fields/RadioField';
import { FileField } from './fields/FileField';
import { RangeField } from './fields/RangeField';
import { ArrayField } from './fields/ArrayField';
import { GroupField } from './fields/GroupField';
import { PhoneField } from './fields/PhoneField';
import { EmailField } from './fields/EmailField';

// Import search and select fields
import { SelectSearchField } from './fields/SelectSearchField';
import { SearchSelectField } from './fields/SearchSelectField';
import { MultiSelectSearchField } from './fields/MultiSelectSearchField';
import { MultiSearchSelectField } from './fields/MultiSearchSelectField';

export const FieldRenderer = ({ 
  field, 
  parentPath = '', 
  register, 
  control, 
  errors, 
  watch, 
  setValue, 
  getValues, 
  theme, 
  customComponents 
}) => {
  if (!field) return null;
  
  const fieldPath = parentPath ? `${parentPath}.${field.id}` : field.id;
  const fieldErrors = parentPath 
    ? getNestedValue(errors, fieldPath)
    : errors[field.id];
  
  const commonProps = {
    field,
    register,
    errors,
    control,
    watch,
    setValue,
    getValues,
    theme
  };
  
  // Custom component
  if (field.component && customComponents[field.component]) {
    const CustomComponent = customComponents[field.component];
    return (
      <FieldWrapper field={field} error={fieldErrors} theme={theme}>
        <CustomComponent {...commonProps} />
      </FieldWrapper>
    );
  }
  
  let FieldComponent;
  let wrapInFieldWrapper = true;
  
  // Select component based on field type
  switch (field.type) {
    case FIELD_TYPES.PASSWORD:
      FieldComponent = <PasswordField {...commonProps} />;
      break;
    case FIELD_TYPES.TEXTAREA:
      FieldComponent = <TextareaField {...commonProps} />;
      break;
    case FIELD_TYPES.SELECT:
      FieldComponent = <SelectField {...commonProps} />;
      break;
    case FIELD_TYPES.CHECKBOX:
      FieldComponent = <CheckboxField {...commonProps} />;
      break;
    case FIELD_TYPES.RADIO:
      FieldComponent = <RadioField {...commonProps} />;
      break;
    case FIELD_TYPES.FILE:
      FieldComponent = <FileField {...commonProps} />;
      break;
    case FIELD_TYPES.RANGE:
      FieldComponent = <RangeField {...commonProps} />;
      break;
    case FIELD_TYPES.ARRAY:
      FieldComponent = <ArrayField {...commonProps} renderField={renderField} />;
      break;
    case FIELD_TYPES.GROUP:
      FieldComponent = <GroupField {...commonProps} renderField={renderField} />;
      wrapInFieldWrapper = false;
      break;
    case FIELD_TYPES.PHONE:  
      FieldComponent = <PhoneField {...commonProps} />;
      break;
    case FIELD_TYPES.TEL:     
      FieldComponent = <PhoneField {...commonProps} />;
      break;
    case FIELD_TYPES.EMAIL:     
      FieldComponent = <EmailField {...commonProps} />;
      break;
      
    // SINGLE SELECT SEARCH FIELD TYPES
    case FIELD_TYPES.SELECT_SEARCH:
      FieldComponent = (
        <SelectSearchField 
          {...commonProps}
          placeholder={field.placeholder}
          options={field.options || []}
          // Spread searchFieldProps if they exist
          {...(field.searchFieldProps || {})}
        />
      );
      break;
      
    case FIELD_TYPES.SEARCH_SELECT:
      FieldComponent = (
        <SearchSelectField 
          {...commonProps}
          placeholder={field.placeholder}
          searchEndpoint={field.searchEndpoint}
          // Spread searchFieldProps if they exist
          {...(field.searchFieldProps || {})}
        />
      );
      break;
      
    // MULTI SELECT SEARCH FIELD TYPES
    case FIELD_TYPES.MULTI_SELECT_SEARCH:
      FieldComponent = (
        <MultiSelectSearchField 
          {...commonProps}
          placeholder={field.placeholder}
          options={field.options || []}
          // Spread multiSelectProps if they exist
          {...(field.multiSelectProps || {})}
        />
      );
      break;
      
    case FIELD_TYPES.MULTI_SEARCH_SELECT:
      FieldComponent = (
        <MultiSearchSelectField 
          {...commonProps}
          placeholder={field.placeholder}
          searchEndpoint={field.searchEndpoint}
          // Spread multiSelectProps if they exist
          {...(field.multiSelectProps || {})}
        />
      );
      break;
      
    default:
      FieldComponent = <TextField {...commonProps} />;
  }
  
  // Helper function for recursive rendering (for ArrayField and GroupField)
  function renderField(subField, subPath) {
    return (
      <FieldRenderer
        field={subField}
        parentPath={subPath}
        register={register}
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        theme={theme}
        customComponents={customComponents}
      />
    );
  }
  
  if (wrapInFieldWrapper) {
    return (
      <FieldWrapper field={field} error={fieldErrors} theme={theme}>
        {FieldComponent}
      </FieldWrapper>
    );
  }
  
  return FieldComponent;
};

export default FieldRenderer;